// src/contexts/AuthContext.tsx
import React, { createContext, useCallback, useContext, useState, ReactNode } from 'react';
import { clearDemoMode, isDemoModeEnabled } from '../utils/demoMode';
import { getCampaignAttribution, salsaAnalytics } from '../services/salsaAnalytics';

export interface TrialInfo {
  status: 'trialing' | 'expired' | 'paid';
  trial_days: number;
  reminder_days: number;
  started_at: string | null;
  ends_at: string | null;
  days_remaining: number;
  should_notify: boolean;
  requires_payment: boolean;
}

interface UserInfo {
  id: string;
  firstname: string;
  lastname: string;
  company: string;
  email: string;
  avatar?: string;
  role?: string;
  trial?: TrialInfo;
  email_mfa_enabled?: boolean;
}

export interface EmailMfaChallenge {
  challengeId: string;
  maskedEmail: string;
  expiresAt?: string;
}

interface PlanInfo {
  selectedPlan?: string;
  billingCycle?: 'monthly' | 'yearly';
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: UserInfo | null;
  isLoading: boolean;
  hasCheckedAuth: boolean;
  checkAuthStatus: () => Promise<void>;
  login: (email: string, password: string) => Promise<EmailMfaChallenge | null>;
  verifyEmailMfaLogin: (challengeId: string, code: string) => Promise<void>;
  resendEmailMfaLogin: () => Promise<EmailMfaChallenge>;
  logout: () => Promise<void>;
  register: (email: string, password: string, firstname?: string, lastname?: string, company?: string, planInfo?: PlanInfo) => Promise<void>;
  updateUser: (userData: { firstname?: string; lastname?: string; company?: string; password?: string }) => Promise<{ message: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

const clearSessionAuthOverride = () => {
  clearDemoMode();
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<UserInfo | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasCheckedAuth, setHasCheckedAuth] = useState<boolean>(false);

  // Get CSRF token
  const getCsrfToken = async (): Promise<string> => {
    const response = await fetch('/api/csrf-token', {
      credentials: 'include'
    });
    const data = await response.json();
    return data.csrfToken;
  };

  const login = async (email: string, password: string): Promise<EmailMfaChallenge | null> => {
    try {
      setIsLoading(true);
      
      const csrfToken = await getCsrfToken();
      
      const response = await fetch('/api/login', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success && data.mfa_required) {
        return {
          challengeId: data.challenge_id,
          maskedEmail: data.masked_email,
          expiresAt: data.expires_at,
        };
      }

      if (response.ok && data.success) {
        clearSessionAuthOverride();
        setUser(data.user);
        setIsAuthenticated(true);
        return null;
      } else {
        throw new Error(data.message || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const verifyEmailMfaLogin = async (challengeId: string, code: string): Promise<void> => {
    try {
      setIsLoading(true);
      const csrfToken = await getCsrfToken();
      const response = await fetch('/api/auth/email-mfa/verify-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        credentials: 'include',
        body: JSON.stringify({ challenge_id: challengeId, code }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Verification failed');
      clearSessionAuthOverride();
      setUser(data.user);
      setIsAuthenticated(true);
    } finally {
      setIsLoading(false);
    }
  };

  const resendEmailMfaLogin = async (): Promise<EmailMfaChallenge> => {
    const csrfToken = await getCsrfToken();
    const response = await fetch('/api/auth/email-mfa/resend-login', {
      method: 'POST',
      headers: { 'X-CSRF-Token': csrfToken },
      credentials: 'include',
    });
    const data = await response.json();
    if (!response.ok || !data.success) throw new Error(data.message || 'Unable to send a new code');
    return { challengeId: data.challenge_id, maskedEmail: data.masked_email, expiresAt: data.expires_at };
  };

  const register = async (email: string, password: string, firstname?: string, lastname?: string, company?: string, planInfo?: PlanInfo): Promise<void> => {
    try {
      setIsLoading(true);
      
      const csrfToken = await getCsrfToken();
      
      const response = await fetch('/api/register', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        credentials: 'include',
        body: JSON.stringify({ 
          email, 
          password, 
          firstname, 
          lastname, 
          company,
          planInfo,
          attribution: getCampaignAttribution(),
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        clearSessionAuthOverride();
        setUser(data.user);
        setIsAuthenticated(true);
        salsaAnalytics.identify(data.user.id, {
          email_domain: String(data.user.email || '').split('@')[1] || 'unknown',
          ...getCampaignAttribution(),
        });
        salsaAnalytics.track('onzoe_signup_completed', {
          user_id: data.user.id,
          selected_plan: planInfo?.selectedPlan,
          billing_cycle: planInfo?.billingCycle,
          ...getCampaignAttribution(),
        });
      } else {
        throw new Error(data.message || 'Registration failed');
      }
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      const csrfToken = await getCsrfToken();
      
      await fetch('/api/logout', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken
        },
        credentials: 'include'
      });
      
      clearSessionAuthOverride();
      setIsAuthenticated(false);
      setUser(null);
      
      // Redirect to home page
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
      // Still clear local state even if request fails
      clearSessionAuthOverride();
      setIsAuthenticated(false);
      setUser(null);
      window.location.href = '/';
    }
  };

  const updateUser = async (userData: { firstname?: string; lastname?: string; company?: string; password?: string }): Promise<{ message: string }> => {
    try {
      const csrfToken = await getCsrfToken();
      
      const response = await fetch('/api/profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        credentials: 'include',
        body: JSON.stringify(userData)
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setUser(data.user);
        return { message: data.message };
      } else {
        throw new Error(data.message || 'Update failed');
      }
    } catch (error) {
      console.error('Update user error:', error);
      throw error;
    }
  };

  // Check authentication status
  const checkAuthStatus = useCallback(async () => {
    setIsLoading(true);

    try {
      // Check for test authentication first
      const testAuth = isDemoModeEnabled() ? sessionStorage.getItem('isAuthenticated') : null;
      const testUserData = isDemoModeEnabled() ? sessionStorage.getItem('userData') : null;
      
      if (testAuth === 'true' && testUserData) {
        const userData = JSON.parse(testUserData);
        setUser(userData);
        setIsAuthenticated(true);
        setHasCheckedAuth(true);
        return;
      }
      
      // Regular API-based authentication check
      const response = await fetch('/api/profile', {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setUser(data.user);
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
          setUser(null);
        }
      } else {
        setIsAuthenticated(false);
        setUser(null);
      }
    } catch (error) {
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setHasCheckedAuth(true);
      setIsLoading(false);
    }
  }, []);

  const value: AuthContextType = {
    isAuthenticated,
    user,
    isLoading,
    hasCheckedAuth,
    checkAuthStatus,
    login,
    verifyEmailMfaLogin,
    resendEmailMfaLogin,
    logout,
    register,
    updateUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
